import { useEffect, useRef } from 'react'
import { Lock, Check } from 'lucide-react'
import type { PickableMatchday } from '@/hooks/usePickableMatchdays'

interface MatchdaySelectorProps {
  matchdays: PickableMatchday[]
  selectedMatchday: number
  onSelect: (matchday: number) => void
}

// A full season of 38 chips is noise — cap the strip at the recent past
// (completed/locked, already limited upstream) plus the next few upcoming
// matchdays. The Fixtures tab still lists the whole season.
const UPCOMING_LIMIT = 6

export function MatchdaySelector({ matchdays, selectedMatchday, onSelect }: MatchdaySelectorProps) {
  const selectedRef = useRef<HTMLButtonElement>(null)
  const scrollerRef = useRef<HTMLDivElement>(null)

  // Scroll the strip container directly rather than scrollIntoView, which
  // also scrolls ancestor containers and would yank the page back to the
  // strip when the selection changes while the user is scrolled down.
  useEffect(() => {
    const chip = selectedRef.current
    const scroller = scrollerRef.current
    if (!chip || !scroller) return
    const chipRect = chip.getBoundingClientRect()
    const scrollerRect = scroller.getBoundingClientRect()
    const chipCenter = scroller.scrollLeft + (chipRect.left - scrollerRect.left) + chipRect.width / 2
    scroller.scrollLeft = Math.max(0, chipCenter - scroller.clientWidth / 2)
  }, [selectedMatchday, matchdays.length])

  let upcomingShown = 0
  const visibleMatchdays = matchdays.filter((md) => {
    if (md.isCompleted || md.isLocked) return true
    if (upcomingShown < UPCOMING_LIMIT) {
      upcomingShown++
      return true
    }
    return md.matchday === selectedMatchday
  })

  if (visibleMatchdays.length <= 1) return null

  return (
    // justify-center on the scroll container clips the leading chips once the
    // strip overflows (scrollLeft can't go negative), so a w-max inner wrapper
    // centers short strips while long strips scroll normally from MD 1.
    <div ref={scrollerRef} className="overflow-x-auto pb-1 scrollbar-hide">
      <div className="flex w-max gap-2 px-4 md:px-0 md:mx-auto">
      {visibleMatchdays.map((md) => {
        const isSelected = md.matchday === selectedMatchday
        const baseClasses = 'relative flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors shrink-0'

        let pillClasses: string
        if (isSelected) {
          pillClasses = `${baseClasses} bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900`
        } else if (md.isCompleted) {
          pillClasses = `${baseClasses} bg-zinc-100 text-zinc-400 dark:bg-zinc-800 dark:text-zinc-500`
        } else if (md.isLocked) {
          pillClasses = `${baseClasses} bg-zinc-100 text-zinc-400 dark:bg-zinc-800 dark:text-zinc-500`
        } else {
          pillClasses = `${baseClasses} bg-white text-zinc-700 border border-zinc-200 dark:bg-zinc-900 dark:text-zinc-300 dark:border-zinc-700`
        }

        return (
          <button
            key={md.matchday}
            ref={isSelected ? selectedRef : undefined}
            className={pillClasses}
            onClick={() => onSelect(md.matchday)}
          >
            {md.isCompleted && (
              <Check className="w-3 h-3" />
            )}
            {md.isLocked && !md.isCompleted && (
              <Lock className="w-3 h-3" />
            )}
            <span>MD {md.matchday}</span>
            {md.hasEarlyFixture && !md.isLocked && (
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
            )}
          </button>
        )
      })}
      </div>
    </div>
  )
}
